import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { MapPin, ArrowRight, Phone, MessageCircle, CheckCircle } from "lucide-react";
import { areas as fallbackAreas, services, getBusinessConfig } from "@/lib/site-config";
import { CTABlock } from "@/components/CTABlock";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { seoHead, localBusinessSchema, complianceToBusinessOpts, serviceSchema } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { listAreas } from "@/lib/cms.functions";
import { CmsSections } from "@/components/CmsSections";
import { AreaPageCTAs } from "@/components/AreaPageCTAs";
import { AreaAdviceBlock } from "@/components/AreaAdviceBlock";
import { AreaDriverLinks } from "@/components/AreaDriverLinks";
import { PrimaryCTA } from "@/components/PrimaryCTA";

const driverLinksCopy: Record<string, string> = {
  poplar: "Poplar drivers lean on us for everything from a quick MOT to PCO and private-hire support. Plenty of local Uber, Bolt and minicab drivers are based around Poplar High Street and Aberfeldy Estate, and we keep their cars compliant without unnecessary repairs. Walk-in advice on warning lights, brakes and tyres is always free — pick a service below to see real-world pricing guidance, what we check and how quickly we can usually fit you in. Drop the reg in WhatsApp and we'll quote you back the same day.",
  "canary-wharf": "Canary Wharf drivers use us as the practical alternative to dealer servicing — fair prices, OE-quality parts, and a clear quote before any spanner moves. We see a steady stream of private-hire and high-mileage executive cars from around the Wharf, South Quay and Heron Quays. The links below cover the services Wharf-based drivers ask about most — MOT, diagnostics for warning lights, value-for-money brake jobs and PCO support. Tap one and you'll get the full breakdown and a direct route to call, WhatsApp or book.",
  whitechapel: "Whitechapel drivers — from Commercial Road and Cambridge Heath Road through to the Royal London — typically come to us for an MOT, a fair-priced repair, or PCO/private-hire compliance work. The cluster of services below is what E1 customers ask about most. Each link explains what we actually check, what affects the cost, and how to get a same-week slot. If you'd rather just talk it through, drop your reg on WhatsApp and we'll come back with a clear quote and the next available time — no pressure, no upsell.",
  "brick-lane": "Drivers around Brick Lane, Spitalfields and Bethnal Green Road use us when they want honest, independent garage work without dealer prices or guesswork. We see lots of older diesels, mid-range hatches and a fair share of private-hire vehicles from this end of E1/E2. The shortcuts below cover the high-intent jobs — MOT, diagnostics on warning lights, brakes, tyres and PCO support. Tap one for full guidance and current pricing direction, or send photos of the issue on WhatsApp and we'll take a look before you commit to anything.",
  bow: "Bow drivers along Bow Road, Devons Road and Bow Common rely on us for honest MOT testing, quick brake jobs, diagnostics and PCO compliance work for private-hire cars. We're a short hop via the A11 or Burdett Road and we keep things straight: a written quote before any work, OE-quality parts, and clear advisories so you know what's urgent and what can wait. The links below show the services Bow customers ask about most — each one explains what we check, what really drives the cost and how to get booked in fast.",
  "mile-end": "Mile End customers — from Grove Road and Burdett Road to the streets off Mile End Road — typically come to us for MOTs, brakes, clutches and warning-light diagnostics. We see plenty of high-mileage cars used for delivery driving and private hire, and we'll always tell you straight whether a repair is essential or just nice to have. The links below cover the most common Mile End jobs. Each one explains what's involved, what affects the bill and how to get the work booked in with a free quote first.",
  stratford: "Stratford and Olympic Park drivers reach us in a quick run via the A11 or A12. We're an independent alternative to dealer servicing for everything from a routine MOT through to diagnostics and complex repairs on premium cars. PCO and private-hire drivers from E15 and E20 use us regularly because we keep cars compliant without padding the bill. Tap a service below for full guidance — what we actually check, what drives the cost honestly, and how to get a same-week slot. Or send the reg on WhatsApp for an instant quote.",
  "canning-town": "Canning Town drivers from around Rathbone Market, Silvertown Way and Royal Wharf find us a short drive via the A13 or Lower Lea Crossing. We look after a lot of E16 cars used for work, including private-hire and delivery vehicles that need MOT compliance and brake or tyre work fast. The links below are the jobs Canning Town customers ask about most — MOT, PCO support, diagnostics, value-for-money repairs and tyres. Each one explains what we check, what really drives the price, and how to get booked without back-and-forth.",
  aldgate: "Aldgate and City-edge drivers come to us for a proper independent garage instead of another dealer bill. Many drop the car off before work via Commercial Road or the A13, then collect it later — handy when your day's already packed. We see plenty of executive cars and private-hire vehicles from EC3 and E1. The links below cover MOT, diagnostics, PCO support and value-for-money repair jobs that Aldgate drivers ask about most. Each one explains what's involved, what affects the cost honestly, and the fastest route to call or WhatsApp.",
  blackwall: "Blackwall (E14) drivers are right on our doorstep — minutes via Aspen Way or Preston's Road. We see a lot of Blackwall Tunnel commuters, private-hire drivers and high-mileage cars that need an MOT, a brake refresh or a warning-light diagnosed quickly. The links below cover the most-requested services. Each one explains what we actually check, what changes the price and how to get the work confirmed today rather than next week. If you'd rather not click around, send the reg on WhatsApp and we'll quote you back the same morning.",
};
const DRIVER_LINK_AREAS = new Set(Object.keys(driverLinksCopy));

type AreaRow = { slug: string; name: string; description: string | null; hero_copy: string | null; meta_title: string | null; meta_description: string | null };

// Tailored local context per area
const areaContext: Record<string, {
  localIntro: string;
  whyUs: string[];
  faqs: { q: string; a: string }[];
  nearbyRoads: string;
}> = {
  poplar: {
    localIntro: "Whether you're based near Poplar High Street, East India Dock Road or the surrounding residential streets, we're just minutes away. Many of our regular customers live and work in Poplar and trust us to keep their vehicles in top shape year after year.",
    whyUs: [
      "Walking distance from Poplar DLR — drop your car off and take the train",
      "Trusted by Poplar residents for over a decade",
      "Quick access from East India Dock Road and the A12",
      "Quick turnaround on many MOTs and services where possible",
    ],
    faqs: [
      { q: "How far are you from Poplar High Street?", a: "We're just a few minutes' drive from Poplar High Street. Very easy to reach via East India Dock Road." },
      { q: "Can I walk to you from Poplar DLR?", a: "Yes — we're a short walk from both Poplar and Blackwall DLR stations." },
      { q: "Do you work on vans from local businesses in Poplar?", a: "Absolutely. We service and repair cars, vans and light commercial vehicles. Many local tradespeople use us regularly." },
    ],
    nearbyRoads: "East India Dock Road, Poplar High Street, the A12 and Aspen Way",
  },
  "canary-wharf": {
    localIntro: "Working in Canary Wharf? Drop your car off before you start and pick it up after work. We're a short drive from the estate and many of our customers do exactly that — no disruption to your day.",
    whyUs: [
      "Drop off before work, collect after — we're close to the Wharf",
      "Popular with professionals who want reliable, hassle-free service",
      "Clear communication so you know exactly when your car will be ready",
      "Premium and executive vehicles welcome alongside everyday cars",
    ],
    faqs: [
      { q: "Can I drop my car off before work?", a: "Yes, many Canary Wharf workers drop their car with us first thing and collect it after work. Just call to arrange." },
      { q: "Do you work on premium cars?", a: "Yes — we regularly service and repair Mercedes, BMW, Audi, Porsche and other premium brands. Dealer-quality care without dealer prices." },
      { q: "How do I get to you from Canary Wharf?", a: "We're a short drive via Aspen Way or the Limehouse Link. You can also reach us easily via the DLR." },
    ],
    nearbyRoads: "Aspen Way, Westferry Road, Limehouse Link tunnel and the A1261",
  },
  "isle-of-dogs": {
    localIntro: "Living on the Isle of Dogs means you need a garage you can rely on without having to drive far. We're nearby and handle everything from MOTs and routine services to diagnostics and bodywork — all under one roof.",
    whyUs: [
      "Local garage that knows the area — no long drives for routine work",
      "Convenient for Millwall, Mudchute and South Quay residents",
      "Full range of services so you never need to go elsewhere",
      "Friendly, practical team who keep it simple",
    ],
    faqs: [
      { q: "Is it easy to get to you from the Isle of Dogs?", a: "Very easy — a quick drive via Manchester Road or Westferry Road. We're also reachable by DLR." },
      { q: "Do you handle both old and new cars?", a: "Yes, we work on all makes, models and ages. From older vehicles to brand-new ones." },
      { q: "Can you do an MOT and service on the same day?", a: "In most cases yes. Call ahead and we'll schedule both together to save you a trip." },
    ],
    nearbyRoads: "Manchester Road, Westferry Road, Marsh Wall and East Ferry Road",
  },
  limehouse: {
    localIntro: "Limehouse drivers have easy access to our garage via Commercial Road and the surrounding streets. Whether you're coming from Narrow Street, Salmon Lane or further along the A13, we're straightforward to reach and ready to help.",
    whyUs: [
      "Quick and easy access from Commercial Road and the A13",
      "Trusted by Limehouse residents for years",
      "Honest pricing and no unnecessary work",
      "Walk to Limehouse DLR or station while we work on your car",
    ],
    faqs: [
      { q: "How close are you to Limehouse station?", a: "We're a short drive or walk from both Limehouse DLR and the mainline station." },
      { q: "Is parking available when I drop off?", a: "Yes — you can park at the garage when dropping your vehicle off. We'll take it from there." },
      { q: "Do you offer diagnostics for warning lights?", a: "Yes, we have professional-grade diagnostic equipment and can investigate any warning light or fault code." },
    ],
    nearbyRoads: "Commercial Road, Narrow Street, the A13 and Burdett Road",
  },
  docklands: {
    localIntro: "The Docklands area covers a wide stretch of East London, and wherever you are within it, we're well-positioned to help. From Royal Docks and Silvertown through to Canning Town, our garage is easy to reach for any service you need.",
    whyUs: [
      "Centrally located for the wider Docklands area",
      "Full service garage — MOT, servicing, diagnostics, repairs and bodywork",
      "Regular customers from Royal Docks, Silvertown and Canning Town",
      "Professional approach with clear, upfront communication",
    ],
    faqs: [
      { q: "Do you serve the whole Docklands area?", a: "Yes — we regularly serve customers from across Docklands including Royal Docks, Silvertown, Canning Town and Custom House." },
      { q: "Can you handle fleet or business vehicles?", a: "Yes, we work with several local businesses. Call us to discuss fleet servicing or regular maintenance arrangements." },
      { q: "What are your opening hours?", a: "Monday to Friday 9am–6:30pm, Saturday 9am–4pm. Closed Sundays." },
    ],
    nearbyRoads: "Silvertown Way, the A13, Lower Lea Crossing and Royal Albert Way",
  },
  "tower-hamlets": {
    localIntro: "As a local independent garage in Tower Hamlets, we've built our reputation on honest work and fair prices. Whether you're in Bethnal Green, Mile End, Stepney, Bow or Whitechapel, we're easy to reach and ready to help with anything your vehicle needs.",
    whyUs: [
      "Proudly serving Tower Hamlets drivers for years",
      "Easy to reach from Mile End Road, Whitechapel Road and the A11",
      "Strong reputation for honest work and fair pricing",
      "All makes, all models — from daily runarounds to premium motors",
    ],
    faqs: [
      { q: "Where exactly are you in Tower Hamlets?", a: "We're based in the E14 area. Easy to reach from most parts of Tower Hamlets via Mile End Road or Commercial Road." },
      { q: "Do you work on older vehicles?", a: "Yes, we work on vehicles of all ages. Older cars often need more attention and we're experienced in keeping them roadworthy." },
      { q: "Can I get a quote before booking?", a: "Of course. Call us, describe the issue or service you need, and we'll give you a clear idea of cost before you commit." },
    ],
    nearbyRoads: "Mile End Road, Whitechapel Road, Commercial Road and the A11",
  },
  whitechapel: {
    localIntro: "Whitechapel drivers — from Whitechapel Road and Commercial Road to the streets around the Royal London — find us a short drive away. We're a DVSA-authorised MOT station and full-service independent garage, so MOTs, servicing, diagnostics, repairs and bodywork all happen under one roof.",
    whyUs: [
      "Quick to reach from Whitechapel Road and the A11",
      "MOT, servicing and repairs in one trip — no juggling garages",
      "Clear quotes before any work starts",
      "Familiar with the parking and traffic realities of E1",
    ],
    faqs: [
      { q: "How close are you to Whitechapel station?", a: "We're a short drive via Commercial Road. Drop the car off and you can be back at Whitechapel in minutes." },
      { q: "Do you do MOTs for Whitechapel residents same week?", a: "Usually yes — call ahead and we'll find the earliest slot, often within a few days." },
      { q: "Can you service my car and do the MOT together?", a: "Yes. Booking both on the same day saves you a trip and means we spot anything that could affect the MOT." },
    ],
    nearbyRoads: "Whitechapel Road, Commercial Road, the A11 and Cambridge Heath Road",
  },
  "brick-lane": {
    localIntro: "If you live or work around Brick Lane, Spitalfields or the Bethnal Green Road end of E1/E2, we're an easy local choice for honest garage work. Drop the car with us and the area is right there for a coffee or a walk while we work on it.",
    whyUs: [
      "Short drive from Brick Lane and Bethnal Green Road",
      "Independent — no upselling, no dealer mark-up",
      "All makes and models, from city runarounds to executive cars",
      "WhatsApp updates so you're not waiting around in the dark",
    ],
    faqs: [
      { q: "Where can I get an MOT near Brick Lane?", a: "We're one of the nearest independent MOT stations — a short drive south through the back streets or via Commercial Road." },
      { q: "Do you collect cars from Brick Lane?", a: "Collection can sometimes be arranged for local customers — call us and we'll see what works around your day." },
      { q: "Can you quote me before I bring it in?", a: "Yes. Send us details or photos on WhatsApp and we'll give you a guide price before you commit." },
    ],
    nearbyRoads: "Brick Lane, Bethnal Green Road, Commercial Street and Whitechapel High Street",
  },
  bow: {
    localIntro: "Drivers around Bow, Bow Common and Devons Road use us as their go-to local garage. We're a short drive via the A11 or Burdett Road, with a full workshop on site so most jobs are handled in one visit.",
    whyUs: [
      "Easy access from the A11, A12 and Bow Road",
      "DVSA-authorised MOT station for cars and small vans",
      "Honest diagnostics — we explain the fault, not just the cost",
      "Same-day turnaround on many MOTs and brake jobs",
    ],
    faqs: [
      { q: "Is there a good MOT garage near Bow?", a: "Yes — we test cars and small commercial vehicles and we're a short drive from Bow Road." },
      { q: "Can you fix issues found at MOT on the same visit?", a: "Often yes, depending on parts. We'll always quote you first and only do what you approve." },
      { q: "Do you work on diesel cars from Bow?", a: "Yes — diesel servicing, DPF advice and diagnostics are part of our regular work." },
    ],
    nearbyRoads: "Bow Road, the A11, Devons Road and Burdett Road",
  },
  "mile-end": {
    localIntro: "Mile End drivers — from Mile End Road and Grove Road to the side streets off Burdett Road — are right on our doorstep. Whether it's an MOT, a service, brakes or a warning light, we keep things straightforward and local.",
    whyUs: [
      "Easy drive from Mile End Road and Burdett Road",
      "Brakes, clutches and suspension done properly, not bodged",
      "Clear written quotes — no surprises at pickup",
      "Walk to Mile End tube or back home while we work",
    ],
    faqs: [
      { q: "Can I walk home to Mile End after dropping the car?", a: "Yes — many of our regulars do exactly that. Drop the car, walk back, we'll call you when it's ready." },
      { q: "How much is a brake repair near Mile End?", a: "It depends on pads vs discs and the make of car. Call or WhatsApp us and we'll give you a clear price before booking." },
      { q: "Do you do interim and full services?", a: "Yes — interim, full and major services, all stamped in your service book." },
    ],
    nearbyRoads: "Mile End Road, Burdett Road, Grove Road and the A11",
  },
  "bethnal-green": {
    localIntro: "Bethnal Green (E2) is a quick run down to our workshop. We look after a lot of local cars from around Bethnal Green Road, Cambridge Heath Road and Roman Road — MOTs, servicing, diagnostics and the awkward jobs other garages would rather not touch.",
    whyUs: [
      "Short drive from Bethnal Green Road and Cambridge Heath Road",
      "Independent MOT station — fair, transparent testing",
      "Diagnostics that find the real fault, not just the code",
      "Trusted by Bethnal Green regulars for years",
    ],
    faqs: [
      { q: "How far are you from Bethnal Green?", a: "A short drive south through E1/E14 — usually 10–15 minutes depending on traffic." },
      { q: "Can you do an MOT and service same day for E2 customers?", a: "In most cases yes — book ahead and we'll schedule both together." },
      { q: "Do you handle older cars from Bethnal Green?", a: "Yes — older cars often need a careful eye and we're happy to keep them on the road." },
    ],
    nearbyRoads: "Bethnal Green Road, Cambridge Heath Road, Roman Road and the A1208",
  },
  wapping: {
    localIntro: "Wapping (E1W) is one of our closest neighbourhoods. From the high street and Wapping Lane down to the riverside, we're a short drive away — handy when you don't want a half-day round trip just to sort an MOT or a service.",
    whyUs: [
      "Very close to Wapping — minimal travel time",
      "MOT, servicing and bodywork in one local garage",
      "Bodywork repairs and colour matching done in-house",
      "Friendly team who explain the work in plain English",
    ],
    faqs: [
      { q: "Where's the nearest MOT centre to Wapping?", a: "We're one of the closest authorised MOT stations — a short drive via The Highway." },
      { q: "Can you repair scratches and dents from car park knocks?", a: "Yes — bodywork repairs and colour matching are done in-house at competitive prices." },
      { q: "Do you work on premium cars from Wapping?", a: "Yes, we regularly service premium brands without dealer prices." },
    ],
    nearbyRoads: "The Highway, Wapping Lane, Cable Street and Commercial Road",
  },
  stratford: {
    localIntro: "Stratford (E15/E20) drivers can reach us in a short drive via the A11 or A12. We're a practical alternative to dealer servicing for anyone in and around Westfield, the Olympic Park or the residential streets of E15.",
    whyUs: [
      "Quick drive from Stratford via the A11 or A12",
      "Independent servicing for all makes — including premium",
      "Diagnostics for warning lights and electrical faults",
      "Honest advice before any work goes ahead",
    ],
    faqs: [
      { q: "Can I get an MOT near Stratford?", a: "Yes — drop the car with us in the morning and pick it up later. Easy run back to Stratford via the A11/A12." },
      { q: "Do you service hybrid cars from Stratford?", a: "We service most hybrid models — call us with your make/model and we'll confirm." },
      { q: "Is it cheaper than the main dealer?", a: "Almost always, while keeping your warranty intact through proper servicing." },
    ],
    nearbyRoads: "Stratford High Street, the A11, the A12 and Romford Road",
  },
  stepney: {
    localIntro: "Stepney and Stepney Green are right on our doorstep. We're a short drive away and look after a lot of regular customers from the streets around Stepney Way and Stepney Green Park.",
    whyUs: [
      "Very local — short drive from Stepney",
      "DVSA-authorised MOT testing on site",
      "Servicing and repairs for cars and small vans",
      "No upselling — we only quote for what you actually need",
    ],
    faqs: [
      { q: "How close are you to Stepney Green?", a: "A short drive south — many Stepney customers drop the car off and walk back home." },
      { q: "Can you handle clutch and gearbox work?", a: "Yes — we regularly do clutch replacements and gearbox repairs with quality parts." },
      { q: "Do you offer fair MOT pricing?", a: "We work to standard DVSA MOT fees with no hidden extras." },
    ],
    nearbyRoads: "Stepney Way, Stepney Green, Commercial Road and Mile End Road",
  },
  shadwell: {
    localIntro: "Shadwell (E1) drivers find us easy to reach via The Highway or Commercial Road. We handle MOTs, servicing, diagnostics and repairs without the long drive across town that some big-name chains demand.",
    whyUs: [
      "Short drive from Shadwell via The Highway or Cable Street",
      "Quick MOT and service slots for local drivers",
      "Diagnostics that explain the fault clearly",
      "All work guaranteed and quoted upfront",
    ],
    faqs: [
      { q: "Is there a garage near Shadwell DLR?", a: "We're a short drive — drop the car, take the DLR, and we'll call you when it's done." },
      { q: "Can you sort a warning light same day?", a: "Often yes — diagnostics usually take under an hour; repair times depend on parts." },
      { q: "Do you do MOTs for vans?", a: "Yes — class 4 and 7 MOTs for cars and small commercial vehicles." },
    ],
    nearbyRoads: "The Highway, Cable Street, Commercial Road and Cannon Street Road",
  },
  aldgate: {
    localIntro: "Aldgate (EC3/E1) is a short drive away via Commercial Road. We're a popular local choice for City workers and Aldgate residents who want a proper independent garage rather than another dealer bill.",
    whyUs: [
      "Easy run from Aldgate via Commercial Road or the A13",
      "Drop off before work, collect later in the day",
      "Independent — fair prices, no dealer mark-up",
      "Clear WhatsApp updates so you can plan your day",
    ],
    faqs: [
      { q: "Can I drop my car off before work in the City?", a: "Yes — many Aldgate and City customers do exactly that. Call to confirm timings." },
      { q: "Do you offer collection for Aldgate?", a: "Sometimes — depending on staff availability. Call us and we'll see what we can do." },
      { q: "Are you cheaper than dealer servicing?", a: "Almost always, while still using quality parts and protecting your warranty." },
    ],
    nearbyRoads: "Commercial Road, Whitechapel High Street, the A13 and Leman Street",
  },
  "canning-town": {
    localIntro: "Canning Town (E16) drivers can reach us in a short drive via the A13 or the Lower Lea Crossing. We're a practical, no-nonsense garage choice for anyone around Rathbone Market, Silvertown Way or Royal Wharf.",
    whyUs: [
      "Quick drive from Canning Town via the A13",
      "MOT, servicing and bodywork in one place",
      "Honest diagnostics for warning lights and faults",
      "Cars and small vans both welcome",
    ],
    faqs: [
      { q: "How long does it take to drive from Canning Town?", a: "Usually 10–15 minutes via the A13 or Lower Lea Crossing." },
      { q: "Can you do bodywork repairs for Canning Town cars?", a: "Yes — scratches, dents, bumper scuffs and colour-matched panel work, all done in-house." },
      { q: "Do you handle business vehicles?", a: "Yes — we work with several local businesses on fleet servicing and MOTs." },
    ],
    nearbyRoads: "Silvertown Way, the A13, Lower Lea Crossing and Barking Road",
  },
  blackwall: {
    localIntro: "Blackwall (E14) is one of our closest postcodes. Drivers from around Blackwall Way, Aspen Way and the Blackwall Tunnel approach are minutes from our workshop — handy when an MOT or repair is the last thing you want to spend half a day on.",
    whyUs: [
      "Minutes from Blackwall via Aspen Way",
      "DVSA-authorised MOT station for cars and small vans",
      "Full mechanical and bodywork facilities under one roof",
      "Regular customers from across E14",
    ],
    faqs: [
      { q: "Where's the nearest MOT to Blackwall?", a: "We're one of the closest authorised MOT stations — a short drive via Aspen Way." },
      { q: "Can you sort a tyre and an MOT in one visit?", a: "Yes — tyres, MOTs and many other jobs can be combined to save you a trip." },
      { q: "Do you handle Blackwall Tunnel commuters' cars?", a: "Absolutely — we see plenty of cars worked hard on tunnel commutes and know what to look for." },
    ],
    nearbyRoads: "Aspen Way, Blackwall Way, the A1261 and Preston's Road",
  },
};

export const Route = createFileRoute("/areas/$area")({
  loader: async ({ params }) => {
    const [cfg, areasRes] = await Promise.all([
      getPublicConfig().catch(() => null),
      listAreas().catch(() => ({ items: [] as AreaRow[] })),
    ]);
    const dbRows = (areasRes.items as AreaRow[]) ?? [];
    const dbArea = dbRows.find((a) => a.slug === params.area) ?? null;
    const fb = fallbackAreas.find((a) => a.slug === params.area);
    const merged = dbArea
      ? {
          slug: dbArea.slug,
          name: dbArea.name,
          description: dbArea.description ?? fb?.description ?? "",
          hero_copy: dbArea.hero_copy ?? null,
          meta_title: dbArea.meta_title ?? null,
          meta_description: dbArea.meta_description ?? null,
        }
      : fb
        ? { slug: fb.slug, name: fb.name, description: fb.description, hero_copy: null, meta_title: null, meta_description: null }
        : null;
    return { compliance: cfg?.compliance || null, areaSlug: params.area, area: merged };
  },
  head: ({ params, loaderData }) => {
    const area = loaderData?.area;
    const name = area?.name ?? params.area;
    const ctx = areaContext[params.area];
    const jsonLd: Record<string, unknown>[] = [
      localBusinessSchema({
        ...complianceToBusinessOpts(loaderData?.compliance),
        serviceCoverage: [name, ...(complianceToBusinessOpts(loaderData?.compliance).serviceCoverage || [])],
      }),
      serviceSchema({
        name: `Garage Services in ${name}`,
        serviceType: "AutoRepair",
        description: `MOT testing, servicing, diagnostics, mechanical repairs and bodywork for drivers in ${name}, East London.`,
        areaServed: `${name}, London`,
      }),
    ];
    if (ctx?.faqs?.length) {
      jsonLd.push(faqSchema(ctx.faqs) as Record<string, unknown>);
    }
    return seoHead({
      path: `/areas/${params.area}`,
      title: area?.meta_title || `Garage in ${name} — MOT & Servicing | Woodseer Autos`,
      description: area?.meta_description || `Trusted garage near ${name}, East London — MOT, servicing, diagnostics, repairs and bodywork. Honest pricing. Call ${NAP.phone}.`,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Areas", path: "/areas" },
        { name, path: `/areas/${params.area}` },
      ],
      jsonLd,
    });
  },
  component: AreaPage,
  notFoundComponent: () => (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-foreground">Area Not Found</h1>
        <p className="mt-2 text-muted-foreground">We couldn't find that area page.</p>
        <Link to="/areas" className="mt-4 inline-block text-primary hover:text-primary/80">View all areas →</Link>
      </div>
    </div>
  ),
});

function AreaPage() {
  const { area: areaSlug } = Route.useParams();
  const { area } = Route.useLoaderData();
  const biz = getBusinessConfig();

  if (!area) {
    throw notFound();
  }

  const ctx = areaContext[area.slug];

  return (
    <>
      {/* Hero */}
      <section className="py-12 sm:py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <div className="inline-flex items-center gap-2 text-primary text-sm font-medium mb-4">
            <MapPin className="h-4 w-4" />
            {area.name}, East London
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground tracking-tight">
            Trusted Garage Near {area.name}
          </h1>
          <p className="mt-5 sm:mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {area.hero_copy || area.description}
          </p>
          {ctx && !area.hero_copy && (
            <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {ctx.localIntro}
            </p>
          )}
          <div className="mt-8 flex justify-center">
            <PrimaryCTA location={`area_${area.slug}_hero`} intent={`quote near ${area.name}`} variant="light" />
          </div>
          {ctx && (
            <p className="mt-5 text-xs text-muted-foreground">
              Easy to reach from {ctx.nearbyRoads}
            </p>
          )}
        </div>
      </section>

      <ReassuranceBanner />

      <AreaPageCTAs areaSlug={area.slug} areaName={area.name} />

      {/* Per-area CMS overrides — admins can append/replace blocks via "Page Sections" */}
      <CmsSections pageSlug={`areas/${area.slug}`} />

      {/* Why drivers in this area choose us */}
      {ctx && (
        <AnimatedSection>
          <section className="py-16 md:py-20 bg-card">
            <div className="mx-auto max-w-4xl px-4">
              <h2 className="text-3xl font-bold text-foreground text-center">Why {area.name} Drivers Choose Us</h2>
              <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {ctx.whyUs.map((reason) => (
                  <StaggerItem key={reason}>
                    <div className="flex items-start gap-3 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      {reason}
                    </div>
                  </StaggerItem>
                ))}
              </StaggerContainer>
            </div>
          </section>
        </AnimatedSection>
      )}

      {/* Services */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Services Available Near {area.name}</h2>
            <p className="mt-3 text-center text-muted-foreground">Everything your vehicle needs — MOTs, servicing, diagnostics, repairs and bodywork.</p>
            <StaggerContainer className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s) => (
                <StaggerItem key={s.slug}>
                  <Link
                    to="/areas/$area/$service"
                    params={{ area: area.slug, service: s.slug }}
                    className="group flex flex-col rounded-xl border border-border bg-card p-5 hover:border-primary/40 transition-all"
                  >
                    <h3 className="text-lg font-semibold text-foreground">{s.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground flex-1">{s.shortDesc}</p>
                    <div className="mt-3 flex items-center gap-1 text-sm font-medium text-primary">
                      {s.name} near {area.name}
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Local FAQs */}
      {ctx && (
        <AnimatedSection>
          <FAQAccordion faqs={ctx.faqs} title={`FAQs for ${area.name} Drivers`} />
        </AnimatedSection>
      )}

      <AreaAdviceBlock areaSlug={area.slug} areaName={area.name} />

      {DRIVER_LINK_AREAS.has(area.slug) && (
        <AreaDriverLinks
          areaSlug={area.slug}
          areaName={area.name}
          intro={driverLinksCopy[area.slug]}
        />
      )}





      {/* Other areas */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-5xl px-4">
            <h2 className="text-2xl font-bold text-foreground text-center">We Also Serve</h2>
            <p className="mt-2 text-center text-muted-foreground">Drivers from across East London trust Woodseer Autos.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {fallbackAreas.filter((a) => a.slug !== area.slug).map((a) => (
                <Link
                  key={a.slug}
                  to="/areas/$area"
                  params={{ area: a.slug }}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-border bg-background text-sm font-medium text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  {a.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      <CTABlock headline={`Need a Garage Near ${area.name}?`} subtext="We're just a short drive away. Give us a call and we'll sort your vehicle out — no pressure, no obligations." />
    </>
  );
}
